import { callApi } from './api';
import Product from '@/models/product';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const accessoriesApiUrl = import.meta.env.VITE_API_URL_V1 + '/accessories';
// Admin reads go through `/accessories/admin/*` (admin-role gated). POST/PUT/DELETE on
// `/accessories/products*` are already admin-gated.
const accessoriesAdminApiUrl = `${accessoriesApiUrl}/admin`;

/** One selectable Stripe price for the product → price mapping dropdown. */
export interface StripePriceOption {
    id: string;
    productId: string;
    productName: string;
    unitAmount: number | null;
    currency: string;
    nickname: string | null;
    recurring: { interval: string; interval_count: number } | null;
    formatted: string | null;
}

export const getProducts = async () => {
    return callApi<Product[]>({ url: `${accessoriesAdminApiUrl}/products`, method: 'GET' });
};

export const useProducts = () => {
    return useQuery({ queryKey: ['products'], queryFn: getProducts });
};

export const getProduct = async (productId: string) => {
    return callApi<Product>({ url: `${accessoriesAdminApiUrl}/products/${productId}`, method: 'GET' });
};

export const useProduct = (productId: string, isOpen: boolean) => {
    return useQuery({
        queryKey: ['product', productId],
        queryFn: () => getProduct(productId),
        enabled: !!productId && isOpen,
    });
};

export const getStripePrices = async () => {
    return callApi<StripePriceOption[]>({ url: `${accessoriesAdminApiUrl}/stripe-prices`, method: 'GET' });
};

export const useStripePrices = (isOpen: boolean) => {
    return useQuery({ queryKey: ['stripePrices'], queryFn: getStripePrices, enabled: isOpen });
};

/**
 * Only editable fields go over the wire (the API's validate() rejects unmatched
 * body keys). Pricing is never sent — only the `stripePriceId` reference.
 */
export type ProductMutationPayload = {
    slug: string;
    name: string;
    description: string;
    tagline?: string;
    stripePriceId?: string;
    stripeProductId?: string;
    image?: string;
    images?: string[];
    comingSoon?: boolean;
    active?: boolean;
    orderIndex?: number;
};

export const createProduct = async (payload: ProductMutationPayload) => {
    return callApi<Product>({ url: `${accessoriesApiUrl}/products`, method: 'POST', data: payload });
};

export const useCreateProduct = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createProduct,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['products'] });
        },
    });
};

export const updateProduct = async ({ productId, ...payload }: ProductMutationPayload & { productId: string }) => {
    return callApi<Product>({ url: `${accessoriesApiUrl}/products/${productId}`, method: 'PUT', data: payload });
};

export const useUpdateProduct = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateProduct,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.invalidateQueries({ queryKey: ['product', variables.productId] });
        },
    });
};

export const deleteProduct = async ({ productId }: { productId: string }) => {
    return callApi({ url: `${accessoriesApiUrl}/products/${productId}`, method: 'DELETE' });
};

export const useDeleteProduct = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteProduct,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['products'] });
        },
    });
};
